import { z } from 'zod'
import { SimulatingPostTGEPop } from '../token-designer/adjusting.types'
import { DemandDrivers, DistributionGroups } from '../token-designer/modeling.types'
import { EcosystemUser } from '../token-designer/token-profile.types'
import { ComparableConfigurations } from '../token-designer/valuation.types'

/**
 * Design Tokenomics (token designer v2) works on a flat model document instead of the
 * `sections -> subSections -> fields` tree v1 stores in the `td` schema.
 *
 * The same shape serves four roles: the model an anonymous visitor is seeded with, the
 * draft kept in the browser, the body of the stateless dashboard computation, and later
 * the payload of an admin-managed template.
 */
export const DesignModelSlug = z.enum(['forgd-recommended', 'low-float-high-fdv', 'defi-demo'])
export type DesignModelSlug = z.infer<typeof DesignModelSlug>

export const DesignModel = z.object({
  slug: DesignModelSlug,
  name: z.string(),
  image: z.string().nullable(),
  FDV: z.number(),
  MTS: z.number(),
  AIC: z.number(),
  groups: DistributionGroups,
  demandDrivers: DemandDrivers,
  comparables: ComparableConfigurations,
  postTgePop: SimulatingPostTGEPop,
})
export type DesignModel = z.infer<typeof DesignModel>

export const DesignModelSummary = DesignModel.pick({
  slug: true,
  name: true,
  image: true,
  FDV: true,
  MTS: true,
  AIC: true,
}).extend({
  groupCount: z.number(),
  comparableCount: z.number(),
})
export type DesignModelSummary = z.infer<typeof DesignModelSummary>

export const DesignProfile = z.object({
  summary: z.string().nullable(),
  capitalRaised: z.number().nullable(),
  fdvFromPreviousFundraising: z.number().nullable(),
  founderName: z.string().nullable(),
  founderBio: z.string().nullable(),
  seniorTeamMembers: z.string().nullable(),
  problem: z.string().nullable(),
  solution: z.string().nullable(),
  mission: z.string().nullable(),
  vision: z.string().nullable(),
  valueCreation: z.string().nullable(),
  revenueFrom: z.string().nullable(),
  revenueDenominatedIn: z.string().nullable(),
  revenueTo: z.string().nullable(),
  valueCaptureProtocol: z.string().nullable(),
  valueCaptureToken: z.string().nullable(),
  ecosystemUsers: z.array(EcosystemUser),
})
export type DesignProfile = z.infer<typeof DesignProfile>

export function emptyDesignProfile(): DesignProfile {
  return {
    summary: null,
    capitalRaised: null,
    fdvFromPreviousFundraising: null,
    founderName: null,
    founderBio: null,
    seniorTeamMembers: null,
    problem: null,
    solution: null,
    mission: null,
    vision: null,
    valueCreation: null,
    revenueFrom: null,
    revenueDenominatedIn: null,
    revenueTo: null,
    valueCaptureProtocol: null,
    valueCaptureToken: null,
    ecosystemUsers: [],
  }
}

type ModelValuation = Pick<DesignModel, 'MTS' | 'groups'>

function groupTokens(group: DesignModel['groups'][number], MTS: number): number {
  return ((group.allocationPercentage ?? 0) / 100) * MTS
}

export function anonymousCapitalRaised({ MTS, groups }: ModelValuation): number {
  return groups.reduce((total, group) => total + groupTokens(group, MTS) * (group.costBasisTokens ?? 0), 0)
}

export function anonymousPreviousRoundFdv({ MTS, groups }: ModelValuation): number {
  const highestPrice = groups.reduce((price, group) => Math.max(price, group.costBasisTokens ?? 0), 0)

  return highestPrice * MTS
}

export function anonymousDesignProfile(model: ModelValuation): DesignProfile {
  return {
    ...emptyDesignProfile(),
    capitalRaised: anonymousCapitalRaised(model),
    fdvFromPreviousFundraising: anonymousPreviousRoundFdv(model),
  }
}

/**
 * Project facts shown read-only on Basic Information. They belong to the project, not the design -
 * they are edited in Token Settings - so they are never written back by Design Tokenomics.
 */
export const DesignProjectFacts = z.object({
  ticker: z.string().nullable(),
  name: z.string().nullable(),
  image: z.string().nullable(),
  website: z.string().nullable(),
  sector: z.string().nullable(),
  dateOfTge: z.string().nullable(),
  published: z.boolean(),
})
export type DesignProjectFacts = z.infer<typeof DesignProjectFacts>

export const ANONYMOUS_DESIGN_PROJECT_FACTS: DesignProjectFacts = {
  ticker: 'FRGD',
  name: 'Forgd',
  image: 'https://imagedelivery.net/Lw4fsWz0zmaaaRoXDvl71Q/e28d1d1e-9f40-492c-a18c-0a217ef5d300/public',
  website: 'https://forgd.com',
  sector: 'Memecoin',
  dateOfTge: '2024-09-01',
  published: false,
}

export function emptyDesignProjectFacts(): DesignProjectFacts {
  return {
    ticker: null,
    name: null,
    image: null,
    website: null,
    sector: null,
    dateOfTge: null,
    published: false,
  }
}

export const DesignSectionSlug = z.enum([
  'basic-information',
  'problem-and-solution',
  'mission-vision',
  'value-creation',
  'business-model',
  'product-market-fit',
  'value-capture',
  'token-distribution-schedule',
  'token-emission-schedule',
  'demand-drivers',
  'estimating-demand',
  'fdv-maximum-token-supply',
  'modeling-supply-and-demand',
  'optimizing-circulating-supply',
  'modeling-sell-pressure',
  'simulating-price-discovery',
  'simulating-post-tge-pops',
  'sanitizing-tokenomics',
])
export type DesignSectionSlug = z.infer<typeof DesignSectionSlug>

export const DesignDocument = z.object({
  model: DesignModel,
  profile: DesignProfile,
  project: DesignProjectFacts,
  completed: z.array(DesignSectionSlug),
})
export type DesignDocument = z.infer<typeof DesignDocument>

export const DesignModelSlugPathParam = z.object({
  slug: DesignModelSlug,
})

export const ApplyDesignModelResponse = z.object({
  projectId: z.string().uuid(),
  slug: DesignModelSlug,
})
export type ApplyDesignModelResponse = z.infer<typeof ApplyDesignModelResponse>
